
import { data } from "react-router-dom";
import "./App.css";

import React, { useState, createContext, useContext, useEffect, useMemo } from 'react';

// --- AUTHENTICATION CONTEXT ---
const AuthContext = React.createContext(null);
const useAuth = () => React.useContext(AuthContext); // Custom hook to use AuthContext // for accessing authentication state



// import LandingPage from "./components/LandingPage";

const BRAND_GREEN = '#198754';
const BRAND_RED = '#d14204ff';
const BRAND_DARK = '#157347';
const primaryColorCreate = 'bg-[#198754] hover:bg-green-700';
const primaryColorEdit = 'bg-orange-500 hover:bg-orange-600';

// let new_api_token = '';

const API_TOKEN_KEY = 'new_api_token';

// --- 1. CONTEXT SETUP ---
// Define initial data structure
const initialUserDetails = {
    // API Configuration
    apiToken: '',

    // CV Data Fields
    title: 'Senior Data Engineer CV',
    full_name: 'Jane Doe',
    email: 'jane.doe@example.com',
    phone: '+234 801 234 5678',
    country: 'Nigeria',
    city: 'Lagos',
    home_address: '123 Tech Drive',
    postal_code: '100001',
    linkedin_url: 'https://linkedin.com/in/janedoe',
    portfolio_url: 'https://github.com/janedoe',
    professional_summary: 'Highly experienced and results-driven Senior Data Engineer with 7+ years in building scalable data pipelines, optimizing ETL processes, and migrating complex on-premise systems to cloud platforms (AWS/GCP). Proven ability to drive data governance and implement robust data warehousing solutions.',

    work_experiences: [{ id: 1, job_title: 'Senior Data Engineer', company: 'TechCorp Solutions', location: 'Lagos, Nigeria', dates: '2021 - Present', responsibilities: '• Designed and implemented real-time data ingestion pipelines using Kafka and Flink.\n• Led migration of petabyte-scale data warehouse from on-premise to AWS Redshift, reducing query latency by 40%.\n• Mentored junior engineers on best practices for SQL optimization and Python development.' }],
    educations: [{ id: 1, degree: 'B.Sc Computer Science', institution: 'University of Lagos', location: 'Lagos, Nigeria', dates: '2015 - 2019', achievements: 'Graduated with First Class Honors' }],
    projects: [{ id: 1, name: 'Customer Churn Prediction Model', url: 'https://github.com/janedoe/churn', dates: '2023', summary: 'Developed a machine learning model using PySpark to predict customer churn with 92% accuracy, leading to targeted retention campaigns.' }],
    certifications: [{ id: 1, name: 'AWS Certified Solutions Architect', certificate_id: 'AWS-12345', url: 'https://aws.amazon.com/cert', dates: '2023/02/02', issuing_organization: 'Amazon Web Services', summary: '• Graduated among top 0.1%.\n• Learnt AWS at full-scale' }],

    // Template ID for the API call
    template_id: 3,

    // Placeholder for the CV ID returned by the API after creation
    createdCvId: null,
};

const templates = [
    { id: 1, name: "Simple & Clean", type: "Simple", description: "Classic, single-column design. Minimalist and easy to read." },
    { id: 2, name: "ATS Optimized", type: "ATS", description: "Structured, plain format for maximum Applicant Tracking System compatibility." },
    { id: 3, name: "Modern & Bold", type: "Modern", description: "Visually distinct, two-column layout with a sidebar for contact details." },
];

const CVContext = createContext();

const useCVContext = () => {
    return useContext(CVContext);
};

const CVProvider = ({ children }) => {
    const [userDetails, setUserDetails] = useState(initialUserDetails);

    // ✅ STEP 1: DEFINE the function
    const resetUserDetails = () => {
        // Retain the existing apiToken during a reset, 
        // especially if you are transitioning from one CV to a new one
        // and the user is authenticated (using the Auth Context token).
        const currentToken = userDetails.apiToken;

        // Reset to initial state, keeping the current token if needed
        setUserDetails(prev => ({
            ...initialUserDetails,
            apiToken: currentToken || initialUserDetails.apiToken // Retains token
        }));
    };


    // Effect to persist the apiToken to localStorage whenever it changes in the state
    useEffect(() => {
        if (userDetails.apiToken) {
            localStorage.setItem(API_TOKEN_KEY, userDetails.apiToken);
        } else {
            // Remove token from storage if user clears the input
            localStorage.removeItem(API_TOKEN_KEY);
        }
    }, [userDetails.apiToken]);

    return (
        // ✅ STEP 2: PROVIDE the function
        <CVContext.Provider value={{
            userDetails,
            setUserDetails,
            resetUserDetails, // <-- Add this
            templates // <-- Keep this if templates is defined
        }}>
            {children}
        </CVContext.Provider>
    );
};

// --- GLOBAL UTILITIES ---
const inputClasses = "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#198754] transition placeholder-gray-500";
const sectionInputClasses = "p-2 border border-gray-300 rounded focus:ring-1 focus:ring-[#198754]/50";
const primaryColor = 'bg-[#198754] hover:bg-[#157347] hover:shadow-lg transition duration-200';
const dashedButton = 'w-full p-2 border border-dashed border-[#198754] text-[#198754] rounded-lg hover:bg-[#e6f2ed] transition duration-200';
const secondaryButton = 'px-6 py-2 rounded-full font-semibold text-[#198754] border border-[#198754] hover:bg-[#e6f2ed] transition duration-200';

// Helper function to render bulleted lists from newline-separated text
const ListRenderer = ({ text }) => {
    if (!text) return null;
    const items = text.split('\n').filter(line => line.trim() !== '');
    return (
        <ul className="list-disc ml-5 space-y-1">
            {items.map((item, index) => (
                <li key={index}>{item.replace(/^(\s*[\•\-\*]\s*)/, '')}</li>
            ))}
        </ul>
    );
};

const CV_PURCHASE_BASE_URL = 'https://cv.signup.com.ng/v1/api/cvs';
const PURCHASE_AMOUNT = 1000; // Naira

// Adding an authentication functionality right before users can start filling in their cv details.

const UserDashboardPage = ({ navigate }) => {
    const [currentBalance, setCurrentBalance] = useState(0);

    const { auth, logout } = useAuth();
    // Assuming useCVContext provides a reset function
    const { userDetails, resetUserDetails, /*updateUserBalanceInContext*/ } = useCVContext();

    const [cvs, setCvs] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // --- NEW STATE FOR FILTERING ---
    const [viewFilter, setViewFilter] = useState('all'); // 'all', 'paid', 'draft'
    // --- END NEW STATE FOR FILTERING ---

    // State for deletion confirmation
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [cvToDelete, setCvToDelete] = useState(null); // { id: number, title: string }
    const [isDeleting, setIsDeleting] = useState(false); // Loading state for deletion

    // --- NEW PURCHASE STATES ---
    const [showPurchaseConfirm, setShowPurchaseConfirm] = useState(false);
    const [cvToPurchase, setCvToPurchase] = useState(null); // { id: number, title: string }
    const [isPurchasing, setIsPurchasing] = useState(false); // Loading state for purchasing
    // --- END NEW PURCHASE STATES ---

    // const API_URL = 'https://test.signup.com.ng/v1/api/cvs';
    const API_URL = 'https://cv.signup.com.ng/v1/api/cvs';
    const USER_DETAILS_API_URL = 'https://cv.signup.com.ng/v1/api/auth/me';

    const API_TOKEN = auth.token;

    // --- DEDICATED BALANCE FETCH FUNCTION ---
    const fetchUserBalance = async (token) => {
        if (!token) return;

        try {
            const response = await fetch(USER_DETAILS_API_URL, { // Assuming this endpoint provides user data including balance
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                }
            });

            if (!response.ok) {
                // If fetching user details fails, we might just use the default 0 or context value
                console.error("Failed to fetch user details for balance update.");
                return;
            }

            const data = await response.json();
            // console.log(data.balance)
            // Assuming the balance is at data.user.balance or data.balance
            // Based on common patterns, I'll check both.
            const fetchedBalance = Number(data.balance || data.user?.balance);

            if (!isNaN(fetchedBalance)) {
                setCurrentBalance(fetchedBalance);
            }
        } catch (err) {
            console.error("Error fetching user balance:", err);
            // Optionally set an error visible to the user
        }
    };
    // --- END DEDICATED BALANCE FETCH FUNCTION ---


    // FIX: Define handleEdit outside of useEffect so it's globally accessible in the component
    const handleEdit = (cvId) => {
        // 1. Reset the form context to clear any previous CV data (optional but safer)
        if (resetUserDetails) {
            resetUserDetails();
        }

        // 2. Navigate to the form page (step 1) and pass the cvId
        navigate(1, cvId);
    };

    // --- DELETE FUNCTIONALITY ---

    // 1. Opens the confirmation modal
    const handleConfirmDelete = (cvId, cvTitle) => {
        setCvToDelete({ id: cvId, title: cvTitle });
        setShowDeleteConfirm(true);
    };

    // 2. Performs the actual API DELETE request
    const handleDeleteCV = async () => {
        if (!cvToDelete || !cvToDelete.id || isDeleting) return;

        // Clear previous error message first
        setError(null);
        setIsDeleting(true);
        setShowDeleteConfirm(false); // Close the modal immediately

        try {
            const response = await fetch(`${API_URL}/${cvToDelete.id}`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${API_TOKEN}`,
                    'Content-Type': 'application/json',
                }
            });

            // The API should return a success status (usually 200 or 204)
            if (!response.ok) {
                // If the response is not ok, read the error message if available
                const errorBody = await response.json().catch(() => ({ message: 'Could not delete CV.' }));
                throw new Error(errorBody.message || `HTTP error! Status: ${response.status}`);
            }

            const responseData = await response.json();

            if (!response.ok) {
                throw new Error(responseData.message || `HTTP error! Status: ${response.status}`);
            }

            // Success: CV purchased

            // 1. Update the local state to reflect paid status
            setCvs(prevCvs => prevCvs.map(cv =>
                cv.id === cvToPurchase.id ? { ...cv, paid: true } : cv
            ));

            // 2. CRITICAL FIX: Update the component state directly from the successful response
            const newBalance = Number(responseData.balance);
            if (!isNaN(newBalance)) {
                setCurrentBalance(newBalance);
                // If you have a global context function, call it here:
                // updateGlobalBalance(newBalance);
            }

            // Success: Remove the item from the local state list instantly
            setCvs(prevCvs => prevCvs.filter(cv => cv.id !== cvToDelete.id));

            // Provide user feedback
            setError(`Success! CV "${cvToDelete.title}" (ID: ${cvToDelete.id}) was deleted.`);

        } catch (err) {
            console.error("Failed to delete CV:", err);
            setError(`Error deleting CV: ${err.message}. Please check your token and network connection.`);
        } finally {
            setIsDeleting(false);
            setCvToDelete(null); // Clear the CV to delete after operation
        }
    };

    // 3. Closes the confirmation modal without deleting
    const handleCancelDelete = () => {
        setShowDeleteConfirm(false);
        setCvToDelete(null);
    };

    // --- NEW PURCHASE FUNCTIONALITY ---

    // 1. Opens the purchase confirmation modal
    const handleConfirmPurchase = (cvId, cvTitle) => {
        // Check if the user has enough balance before showing the modal
        if (currentBalance < PURCHASE_AMOUNT) {
            setError(`Error: Insufficient balance. You need ₦${PURCHASE_AMOUNT.toLocaleString()} to purchase this CV.`);
            return;
        }
        setCvToPurchase({ id: cvId, title: cvTitle });
        setShowPurchaseConfirm(true);
    };

    const handlePurchaseCV = async () => {
        if (!cvToPurchase || !cvToPurchase.id || isPurchasing) return;

        setError(null);
        setIsPurchasing(true);
        setShowPurchaseConfirm(false); // Close the modal immediately

        try {
            const response = await fetch(`${CV_PURCHASE_BASE_URL}/${cvToPurchase.id}/purchase`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${API_TOKEN}`,
                    'Content-Type': 'application/json',
                }
            });
            console.log(`${CV_PURCHASE_BASE_URL}/${cvToPurchase.id}/purchase`);

            console.log(response);
            const responseData = await response.json();
            console.log(responseData);

            if (!response.ok) {
                // Handle non-2xx status codes (e.g., balance too low, already paid)
                const errorDetail = responseData.error || responseData.message || `HTTP error! Status: ${response.status}`;
                throw new Error(errorDetail);
            }

            // Success: CV purchased

            // 1. Update the local state to reflect paid status
            setCvs(prevCvs => prevCvs.map(cv =>
                cv.id === cvToPurchase.id ? { ...cv, paid: true } : cv
            ));

            // 2. Update the balance state immediately from the response
            const newBalance = Number(responseData.balance);
            console.log(newBalance);
            setError(`Success! CV "${cvToPurchase.title}" purchased. Your new balance is: ₦${newBalance.toLocaleString()}.`);

        } catch (err) {
            // console.error("Failed to purchase CV:", err);
            // setError(`Error purchasing CV: ${err.message}.`);

            const isInsufficient = err.message.includes('Insufficient balance');
            const userError = isInsufficient
                ? `${err.message}. The required purchase amount is ₦${PURCHASE_AMOUNT.toLocaleString()}. Please check your current balance displayed above.`
                : err.message;

            // Show the user the specific error message (e.g., "Insufficient balance")
            setError(`Error purchasing CV: ${userError}`);
        } finally {
            setIsPurchasing(false);
            setCvToPurchase(null); // Clear the CV to purchase after operation
        }
    };

    // 3. Closes the purchase confirmation modal without purchasing
    const handleCancelPurchase = () => {
        setShowPurchaseConfirm(false);
        setCvToPurchase(null);
    };

     const filteredCvs = useMemo(() => {
        if (!cvs || !Array.isArray(cvs)) return [];

        if (viewFilter === 'paid') {
            return cvs.filter(cv => cv.paid);
        }
        if (viewFilter === 'draft') {
            // Assuming !cv.paid means it's a draft/unpaid
            return cvs.filter(cv => !cv.paid);
        }
        return cvs; // 'all'
    }, [cvs, viewFilter]);

    // --- FETCH EFFECT ---
    useEffect(() => {
        // Only fetch if a token is available
        if (!API_TOKEN) {
            setIsLoading(false);
            setError("Authentication token missing. Please ensure you are properly signed in.");
            return;
        }

        const fetchCvs = async () => {
            setIsLoading(true);
            setError(null); // Clear previous errors

            // 1. Check context for initial balance (if reliable)
            if (userDetails?.balance !== undefined && !isNaN(Number(userDetails.balance))) {
                setCurrentBalance(Number(userDetails.balance));
            } else {
                // 2. Fetch the current balance if not in context or unreliable
                await fetchUserBalance(API_TOKEN);
            }

            // 3. Fetch the list of CVs
            try {
                // Simulate network delay
                await new Promise(resolve => setTimeout(resolve, 1000));

                const response = await fetch(API_URL, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${API_TOKEN}`,
                        'Content-Type': 'application/json',
                    }
                });

                if (!response.ok) {
                    throw new Error(`HTTP error! Status: ${response.status}`);
                }

                const data = await response.json();
                const fetchedCvs = data.data || data;

                if (Array.isArray(fetchedCvs)) {
                    setCvs(fetchedCvs);
                    setError(null);
                } else {
                    throw new Error("API response was not a list of CVs.");
                }

            } catch (err) {
                console.error("Failed to fetch CVs:", err);
                setError(`(Simulated Error: Failed to fetch real data using API. Displaying mock data for structure check.)`);
                setCvs([
                    { id: 1, title: "Mock CV - Senior Software Developer", created_at: "2024-10-15", full_name: "Mock User 1" },
                    { id: 2, title: "Mock CV - Marketing Coordinator", created_at: "2024-09-28", full_name: "Mock User 2" }
                ]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchCvs();
    }, [API_TOKEN]); // Re-run effect if API_TOKEN changes

    return (
        <div className="min-h-screen bg-gray-50 p-4 sm:p-8">
            <div className="max-w-4xl mx-auto bg-white p-6 sm:p-10 rounded-2xl shadow-2xl">
                <div className="flex justify-between items-center mb-8 border-b pb-4">
                    <h2 className="text-4xl font-extrabold text-gray-900" style={{ borderColor: BRAND_GREEN }}>
                        User Dashboard
                    </h2>
                    <div className="text-right p-3 bg-gray-50 rounded-xl shadow-inner border border-teal-200">
                        <p className="text-sm font-semibold text-gray-600">Current Balance:</p>
                        <p className="text-3xl font-bold text-teal-600">₦{currentBalance.toLocaleString()}</p>
                    </div>
                </div>

                <p className="text-sm text-gray-500 mb-6 italic">
                    {/* Showing a List of CVs this particular user with the signed in email has created. */}
                    Manage your created CVs and review your paid copies below.
                </p>

                {/* <div className="grid grid-cols-2">
                    <button onClick={() => navigate(1)} className="text-sm text-[#198754] hover:underline flex items-center mt-2">
                        Click here to Create A New CV
                    </button>
                    <button onClick={() => navigate(1)} className="text-sm text-[#198754] hover:underline flex items-center mt-2">
                        Click here to to see CVs you paid for
                    </button>
                </div> */}

                {/* --- FILTER & ACTION CONTROLS --- */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 space-y-4 sm:space-y-0">
                    {/* Filter Segmented Control */}
                    <div className="flex bg-gray-200 rounded-xl p-1 shadow-inner">
                        <button
                            onClick={() => setViewFilter('all')}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewFilter === 'all' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            All CVs ({cvs.length})
                        </button>
                        <button
                            onClick={() => setViewFilter('paid')}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewFilter === 'paid' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            Paid CVs ({cvs.filter(cv => cv.paid).length})
                        </button>
                        <button
                            onClick={() => setViewFilter('draft')}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all ${viewFilter === 'draft' ? 'bg-white text-gray-900 shadow' : 'text-gray-600 hover:text-gray-900'}`}
                        >
                            Drafts / Unpaid ({cvs.filter(cv => !cv.paid).length})
                        </button>
                    </div>

                    {/* Create New CV Button */}
                    <button
                        onClick={() => navigate(1)}
                        className="text-sm font-semibold text-white px-5 py-2 rounded-lg transition duration-200 shadow-md hover:opacity-90 flex items-center"
                        style={{ backgroundColor: BRAND_GREEN }}
                    >
                        + Create New CV
                    </button>
                </div>
                {/* --- END FILTER & ACTION CONTROLS --- */}
                <br />

                {/* {(isLoading || isDeleting) && (
                    <div className="flex items-center justify-center p-12 text-gray-600">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isDeleting ? 'Processing deletion...' : 'Loading your past CVs...'}
                    </div>
                )} */}
                {/* updating with purchasing */}
                {(isLoading || isDeleting || isPurchasing) && (
                    <div className="flex items-center justify-center p-12 text-gray-600">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        {isDeleting ? 'Processing deletion...' : isPurchasing ? 'Processing purchase...' : 'Loading your past CVs...'}
                    </div>
                )}

                {error && (
                    <div className={`border px-4 py-3 rounded-lg relative mb-4 ${error.includes('Error') ? 'bg-red-100 border-red-400 text-red-700' : 'bg-green-100 border-green-400 text-green-700'}`} role="alert">
                        <strong className="font-bold">{error.includes('Success') ? 'Notification:' : 'Fetch/Action Warning:'}</strong>
                        <span className="block sm:inline ml-2">{error}</span>
                    </div>
                )}

                {/* {!isLoading && cvs.length === 0 && !error && (
                    <p className="text-xl text-gray-500 p-12 text-center border-dashed border-2 rounded-xl">
                        You haven't created any CVs yet. Start building one now!
                    </p>
                )} */}

                 {!isLoading && filteredCvs.length === 0 && !error && (
                    <p className="text-xl text-gray-500 p-12 text-center border-dashed border-2 rounded-xl">
                        {viewFilter === 'paid' && 'You have no purchased CVs yet.'}
                        {viewFilter === 'draft' && 'You have no drafts or unpaid CVs.'}
                        {viewFilter === 'all' && 'You haven\'t created any CVs yet. Start building one now!'}
                    </p>
                )}


                {/* {!isLoading && cvs.length > 0 && (
                    <ul className={`space-y-3 ${(isDeleting || isPurchasing) ? 'opacity-50 pointer-events-none' : ''}`}>
                        {cvs.map((cv) => (

                            <li key={cv.id} className="p-4 border border-gray-200 bg-white rounded-xl hover:shadow-lg transition duration-200 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                                <div className="mb-4 sm:mb-0">
                                    <p className="font-semibold text-lg text-gray-800">
                                        {cv.title || `CV ID: ${cv.id}`}
                                        {cv.paid && (
                                            <span className="ml-3 inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                Paid
                                            </span>
                                        )}
                                        {!cv.paid && (
                                            <span className="ml-3 inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                Unpaid
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-sm text-teal-600">Full Name on CV: {cv.full_name || 'N/A'}</p>
                                    <p className="text-sm text-gray-500">
                                        Created: {new Date(cv.created_at).toLocaleDateString() || 'Unknown Date'} | ID: {cv.id}
                                    </p>
                                </div>

                                <div className="flex space-x-3">
                                    <button
                                        onClick={() => handleEdit(cv.id)}
                                        className="text-sm font-medium px-4 py-2 rounded-lg text-white transition duration-200 shadow-md hover:opacity-90"
                                        style={{ backgroundColor: BRAND_GREEN }}
                                        disabled={isDeleting || isPurchasing}
                                    >
                                        Edit
                                    </button>

                                    {!cv.paid && (
                                        <button
                                            onClick={() => handleConfirmPurchase(cv.id, cv.title || `CV ID: ${cv.id}`)}
                                            className="text-sm font-medium px-4 py-2 rounded-lg text-white transition duration-200 shadow-md hover:opacity-90 bg-blue-600 hover:bg-blue-700"
                                            disabled={isDeleting || isPurchasing}
                                        >
                                            Purchase (₦{PURCHASE_AMOUNT.toLocaleString()})
                                        </button>
                                    )}

                                    <button
                                        onClick={() => handleConfirmDelete(cv.id, cv.title || `CV ID: ${cv.id}`)}
                                        className="text-sm font-medium px-4 py-2 rounded-lg text-white transition duration-200 shadow-md hover:opacity-90 bg-red-600 hover:bg-red-700"
                                        disabled={isDeleting || isPurchasing}
                                    >
                                        Delete CV
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )} */}

                {!isLoading && filteredCvs.length > 0 && (
                    <ul className={`space-y-4 ${(isDeleting || isPurchasing) ? 'opacity-50 pointer-events-none' : ''}`}>
                        {filteredCvs.map((cv) => (

                            <li key={cv.id} className="p-4 border border-gray-200 bg-white rounded-xl hover:shadow-lg transition duration-200 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                                <div className="mb-4 sm:mb-0">
                                    <p className="font-semibold text-lg text-gray-800">
                                        {cv.title || `CV ID: ${cv.id}`}
                                        {cv.paid && (
                                            <span className="ml-3 inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                Paid
                                            </span>
                                        )}
                                        {!cv.paid && (
                                            <span className="ml-3 inline-flex items-center px-3 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                Unpaid / Draft
                                            </span>
                                        )}
                                    </p>
                                    <p className="text-sm text-teal-600">Full Name on CV: {cv.full_name || 'N/A'}</p>
                                    <p className="text-sm text-gray-500">
                                        Created: {new Date(cv.created_at).toLocaleDateString() || 'Unknown Date'} | ID: {cv.id}
                                    </p>
                                </div>

                                <div className="flex space-x-3 flex-shrink-0">
                                    {/* Edit Button */}
                                    <button
                                        onClick={() => handleEdit(cv.id)}
                                        className="text-sm font-medium px-4 py-2 rounded-lg text-white transition duration-200 shadow-md hover:opacity-90"
                                        style={{ backgroundColor: BRAND_GREEN }}
                                        disabled={isDeleting || isPurchasing}
                                    >
                                        Edit
                                    </button>

                                    {/* Purchase Button - Visible only if NOT paid */}
                                    {!cv.paid && (
                                        <button
                                            onClick={() => handleConfirmPurchase(cv.id, cv.title || `CV ID: ${cv.id}`)}
                                            className="text-sm font-medium px-4 py-2 rounded-lg text-white transition duration-200 shadow-md hover:opacity-90 bg-blue-600 hover:bg-blue-700"
                                            disabled={isDeleting || isPurchasing}
                                        >
                                            Purchase (₦{PURCHASE_AMOUNT.toLocaleString()})
                                        </button>
                                    )}

                                    {/* Delete Button */}
                                    <button
                                        onClick={() => handleConfirmDelete(cv.id, cv.title || `CV ID: ${cv.id}`)}
                                        className="text-sm font-medium px-4 py-2 rounded-lg text-white transition duration-200 shadow-md hover:opacity-90 bg-red-600 hover:bg-red-700"
                                        disabled={isDeleting || isPurchasing}
                                    >
                                        Delete CV
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                )}

                <div className="mt-8 pt-6 border-t flex justify-end">
                    <button
                        onClick={() => navigate(1)}
                        className="text-white font-semibold px-6 py-3 rounded-lg shadow-xl transition duration-300 transform hover:scale-[1.02]"
                        style={{ backgroundColor: BRAND_GREEN }}
                        disabled={isDeleting}
                    >
                        Create New CV &rarr;
                    </button>
                </div>
            </div>

            {/* --- CONFIRMATION MODAL UI --- */}
            {showDeleteConfirm && cvToDelete && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4">Confirm Deletion</h3>
                        <p className="text-gray-700 mb-6">
                            Are you sure you want to permanently delete the CV titled:
                            <span className="font-semibold text-red-600 block mt-2 p-2 bg-red-50 rounded-lg">
                                "{cvToDelete.title}" (ID: {cvToDelete.id})
                            </span>
                        </p>
                        <p className="text-sm text-gray-500 mb-8">
                            This action cannot be undone.
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={handleCancelDelete}
                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                                disabled={isDeleting}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteCV}
                                className="px-6 py-2 rounded-lg text-white bg-red-600 hover:bg-red-700 transition font-semibold"
                                disabled={isDeleting}
                            >
                                {isDeleting ? 'Deleting...' : 'Yes, Delete It'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* --- CONFIRMATION MODAL UI (PURCHASE) --- */}
            {showPurchaseConfirm && cvToPurchase && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50 transition-opacity">
                    <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md transform transition-all scale-100">
                        <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                            Confirm CV Purchase
                            <span className="ml-3 text-base font-semibold text-blue-600 bg-blue-100 px-3 py-1 rounded-full">
                                ₦{PURCHASE_AMOUNT.toLocaleString()}
                            </span>
                        </h3>
                        <p className="text-gray-700 mb-4">
                            You are about to purchase the CV titled:
                            <span className="font-semibold text-gray-800 block mt-2 p-2 bg-gray-50 rounded-lg">
                                "{cvToPurchase.title}"
                            </span>
                        </p>
                        <p className="text-sm text-red-500 font-medium mb-6">
                            ⚠️ Please ensure you have completed all editing before purchasing. The transaction will proceed immediately.
                        </p>
                        <div className="flex justify-end space-x-4">
                            <button
                                onClick={handleCancelPurchase}
                                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100 transition"
                                disabled={isPurchasing}
                            >
                                Cancel / Go Back to Edit
                            </button>
                            <button
                                onClick={handlePurchaseCV}
                                className="px-6 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700 transition font-semibold"
                                disabled={isPurchasing}
                            >
                                {isPurchasing ? 'Processing...' : 'Confirm & Pay'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            {/* --- END CONFIRMATION MODAL UI (PURCHASE) --- */}


            {/* --- END CONFIRMATION MODAL UI --- */}
        </div>
    );
};


const CVFormPage = ({ navigate, cvIdToEdit }) => {

    // *** NEW: Get token and isAuthenticated from Auth Context ***
    const { auth, logout } = useAuth();
    // Use the context token


    // Assuming useCVContext provides userDetails, setUserDetails, and resetUserDetails
    const { userDetails, setUserDetails, resetUserDetails } = useCVContext();
    const [formStatus, setFormStatus] = useState(null);
    const [isFetchingData, setIsFetchingData] = useState(false); // New state for loading indicator

    // Determine the mode
    const isEditMode = !!cvIdToEdit;

    // const API_URL = 'https://test.signup.com.ng/v1/api/cvs';
    const API_URL = 'https://cv.signup.com.ng/v1/api/cvs';
    const API_TOKEN = auth.token;

    // const API_TOKEN = userDetails.apiToken; // Token from the form input

    // 1. useEffect for fetching CV data if in Edit Mode (GET /cvs/:id)
    useEffect(() => {
        // Clear status when component mounts or mode changes
        setFormStatus(null);

        if (isEditMode && API_TOKEN) {
            const fetchCvDetails = async () => {
                setIsFetchingData(true);
                try {
                    const response = await fetch(`${API_URL}/${cvIdToEdit}`, {
                        method: 'GET',
                        headers: {
                            'Authorization': `Bearer ${API_TOKEN}`,
                            'Content-Type': 'application/json',
                        }
                    });

                    // if (!response.ok) {
                    //     throw new Error(`Failed to fetch CV details. Status: ${response.status}`);
                    // }

                    if (!response.ok) {
                        // if token is invalid, log user out and move them to login page
                        if (response.status === 401) {
                            logout();
                            navigate('login'); // Redirect to login
                        }
                        throw new Error(`Failed to fetch CV details. Status: ${response.status}`);
                    }

                    const data = await response.json();

                    // The API response structure might wrap the CV data
                    const cvData = data.data || data;

                    // Add the apiToken back to the fetched data before setting
                    const dataToSet = {
                        ...cvData,
                        // Ensure the token from the context is retained, as it's not returned by the GET
                        // apiToken: API_TOKEN
                    };

                    setUserDetails(dataToSet);
                    setFormStatus('success_fetch');

                } catch (err) {
                    console.error("Error fetching CV for edit:", err);
                    setFormStatus('error_fetch');
                } finally {
                    setIsFetchingData(false);
                }
            };
            fetchCvDetails();
        } else if (!isEditMode && resetUserDetails) {
            // If in create mode, ensure the form is cleared (assuming resetUserDetails exists)
            // It's crucial not to call resetUserDetails if in edit mode, as it would clear the token before the fetch.
            resetUserDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cvIdToEdit, API_TOKEN]); // Rerun if token changes

    // Handles changes for simple fields (including the apiToken)
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setUserDetails(prev => ({ ...prev, [name]: value }));
    }

    // Handles changes for array items
    const handleSectionChange = (section, id, field, value) => {
        setUserDetails(prev => ({
            ...prev,
            [section]: (prev[section] || []).map(item =>
                item.id === id ? { ...item, [field]: value } : item
            )
        }));
    };

    // Adds a new entry to any array section
    const handleAddEntry = (section) => {
        const newId = Date.now();
        let newEntry = { id: newId };

        switch (section) {
            case 'work_experiences':
                newEntry = { id: newId, job_title: '', company: '', location: '', dates: '', responsibilities: '' };
                break;
            case 'educations':
                newEntry = { id: newId, degree: '', institution: '', location: '', dates: '', achievements: '' };
                break;
            case 'projects':
                newEntry = { id: newId, name: '', url: '', dates: '', summary: '' };
                break;
            case 'certifications':
                newEntry = { id: newId, name: '', certificate_id: '', url: '', dates: '', issuing_organization: '' };
                break;
            default:
                return;
        }

        setUserDetails(prev => ({
            ...prev,
            [section]: [...(prev[section] || []), newEntry]
        }));
    };

    // Removes an entry from any array section
    const handleRemoveEntry = (section, id) => {
        setUserDetails(prev => ({
            ...prev,
            [section]: (prev[section] || []).filter(item => item.id !== id)
        }));
    };

    // Updated handleFormSubmit for both POST (Create) and PUT (Edit)
    const handleFormSubmit = async (e) => {
        e.preventDefault();

        // --- Validation Check ---
        if (!userDetails.full_name || !userDetails.email || !userDetails.title || !userDetails.professional_summary) {
            setFormStatus('error_validation');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        // --- Edit Mode (PUT) ---
        if (isEditMode) {
            setFormStatus('loading_edit');

            // Prepare the payload (remove temporary client-side keys like 'id' from nested arrays)
            const preparePayload = (data) => {
                const payload = { ...data };
                // delete payload.apiToken; // Don't send token in body

                // Recursively remove the client-side 'id' from arrays
                if (payload.work_experiences) payload.work_experiences = payload.work_experiences.map(({ id, ...rest }) => rest);
                if (payload.educations) payload.educations = payload.educations.map(({ id, ...rest }) => rest);
                if (payload.projects) payload.projects = payload.projects.map(({ id, ...rest }) => rest);
                if (payload.certifications) payload.certifications = payload.certifications.map(({ id, ...rest }) => rest);

                return payload;
            };

            const payload = preparePayload(userDetails);

            try {
                const response = await fetch(`${API_URL}/${cvIdToEdit}`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${API_TOKEN}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload)
                });

                if (!response.ok) {
                    const errorBody = await response.json().catch(() => ({ message: 'Unknown error occurred.' }));
                    throw new Error(errorBody.message || `HTTP error! Status: ${response.status}`);
                }

                // Success: Redirect back to the dashboard (step 4)
                setFormStatus('success_edit');
                setTimeout(() => {
                    navigate(4);
                }, 1500);

            } catch (err) {
                console.error("Error updating CV:", err);
                setFormStatus('error_edit');
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }

        } else {
            // --- Create Mode (POST), proceeds to Step 2 ---
            setFormStatus(null);
            // Save the token to local storage so the dashboard can access it later
            localStorage.setItem('new_api_token', userDetails.apiToken);
            navigate(2);
        }
    };

    // Dynamic rendering variables
    const primaryColor = isEditMode ? primaryColorEdit : primaryColorCreate;
    const headingText = isEditMode ? `Step 1: Edit CV (ID: ${cvIdToEdit})` : 'Step 1: Enter Details';
    const buttonText = isEditMode ? 'Save Changes & Review' : 'Review Data & Select Template (Step 2)';


    if (isFetchingData) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <p className="text-xl font-semibold text-gray-600">Loading CV details for editing...</p>
            </div>
        );
    }

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col items-center p-4 sm:p-8">

            <div className="w-full max-w-3xl">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-extrabold text-gray-900">{headingText}</h1>
                    <span className="text-sm font-semibold text-white bg-[#198754] px-4 py-1 rounded-full shadow-md">
                        {isEditMode ? 'Edit Mode' : '1/3'}
                    </span>
                </div>
                <p className="text-md text-gray-600 mb-8 text-center">
                    {isEditMode
                        ? 'Modify the details below and save your changes. The API Token is required to authenticate the update.'
                        : 'Fill in every section below and provide your authentication token.'}
                </p>
            </div>

            <button onClick={() => navigate(4)} className="text-sm text-[#198754] hover:underline flex items-center mt-2">
                See a list of CVs you have created in the past instead.
            </button>
            <br />

            <form onSubmit={handleFormSubmit} className="bg-white p-6 sm:p-10 rounded-xl shadow-2xl w-full max-w-3xl">

                {/* 
                
                    --- Status Messages ---
                {formStatus === 'error_validation' && (
                    <div className="p-3 mb-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                        Please ensure you have filled out the **API Token** and all required fields before proceeding.
                    </div>
                )}
                {formStatus === 'error_fetch' && (
                    <div className="p-3 mb-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                        Failed to load CV data. Please check your console for details or ensure the token is valid.
                    </div>
                )}
                {formStatus === 'success_fetch' && (
                    <div className="p-3 mb-4 text-sm text-green-800 rounded-lg bg-green-50" role="alert">
                        CV data loaded successfully! Make your changes.
                        {/* <p>Please Note: Users are not allowed to edit some details when editting a CV</p>
                    </div>
                )}
                {formStatus === 'loading_edit' && (
                    <div className="p-3 mb-4 text-sm text-blue-800 rounded-lg bg-blue-50" role="alert">
                        Saving your changes... Please wait.
                    </div>
                )}
                {formStatus === 'success_edit' && (
                    <div className="p-3 mb-4 text-sm text-green-800 rounded-lg bg-green-50" role="alert">
                        CV updated successfully! Redirecting to dashboard.
                    </div>
                )}
                {formStatus === 'error_edit' && (
                    <div className="p-3 mb-4 text-sm text-red-800 rounded-lg bg-red-50" role="alert">
                        Failed to update CV. Please check your token and network connection.
                    </div>
                )}
                */}

                {/* ---> End of commentings */}
                {/* --- End Status Messages --- */}

                {/* API TOKEN INPUT */}
                {/* <h2 className="text-2xl font-bold mb-4 text-gray-800 border-b pb-2">0. API Configuration</h2>
                <div className="space-y-4 mb-8">
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <label className="block text-sm font-medium text-blue-700 mb-1">Authorization Token (from Postman)</label>
                        <input 
                            type="text" 
                            name="apiToken" 
                            value={userDetails.apiToken} 
                            onChange={handleInputChange} 
                            placeholder="Paste your Bearer token here..." 
                            className={inputClasses} 
                            required 
                        />
                        <p className="text-xs text-blue-600 mt-1">This token will be sent in the 'Authorization: Bearer' header to authenticate your request.</p>
                    </div>
                </div> */}


                {/* 1. PERSONAL DETAILS SECTION */}
                <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800 border-b pb-2">1. Personal Details & Summary</h2>
                <div className="space-y-4">
                    <input type="text" name="title" value={userDetails.title} onChange={handleInputChange} placeholder="CV Title (e.g., Senior Staff Data Engineer CV)" className={inputClasses} required />
                    <input type="text" name="full_name" value={userDetails.full_name} onChange={handleInputChange} placeholder="Full Name" className={inputClasses} required disabled={isEditMode} />
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="email" name="email" value={userDetails.email} onChange={handleInputChange} placeholder="Email" className={inputClasses} required disabled={isEditMode} />
                        <input type="text" name="phone" value={userDetails.phone} onChange={handleInputChange} placeholder="Phone Number" className={inputClasses} disabled={isEditMode} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="text" name="country" value={userDetails.country} onChange={handleInputChange} placeholder="Country" className={inputClasses} />
                        <input type="text" name="city" value={userDetails.city} onChange={handleInputChange} placeholder="City" className={inputClasses} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="text" name="home_address" value={userDetails.home_address} onChange={handleInputChange} placeholder="Home Address" className={inputClasses} />
                        <input type="text" name="postal_code" value={userDetails.postal_code} onChange={handleInputChange} placeholder="Postal Code" className={inputClasses} />
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <input type="url" name="linkedin_url" value={userDetails.linkedin_url} onChange={handleInputChange} placeholder="LinkedIn URL" className={inputClasses} />
                        <input type="url" name="portfolio_url" value={userDetails.portfolio_url} onChange={handleInputChange} placeholder="Portfolio URL" className={inputClasses} />
                    </div>
                    <textarea
                        name="professional_summary" value={userDetails.professional_summary} onChange={handleInputChange}
                        placeholder="Professional Summary (Required)"
                        rows="5" className={`${inputClasses} resize-y`} required
                    />
                </div>

                {/* 2. WORK EXPERIENCE SECTION */}
                <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800 border-b pb-2">2. Work Experience</h2>
                <div className="space-y-6">
                    {(userDetails.work_experiences || []).map((job) => (
                        <div key={job.id} className="p-5 border border-gray-200 rounded-xl bg-gray-50 relative shadow-sm">
                            <button type="button" onClick={() => handleRemoveEntry('work_experiences', job.id)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition duration-150 p-1 rounded-full bg-white" title="Remove Experience Entry">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 10-2 0v6a1 1 0 102 0V8z" clipRule="evenodd" /></svg>
                            </button>
                            <h3 className="text-lg font-semibold mb-3 text-gray-700">Role: {job.job_title || 'New Role'}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <input type="text" value={job.job_title} onChange={(e) => handleSectionChange('work_experiences', job.id, 'job_title', e.target.value)} placeholder="Job Title" className={sectionInputClasses} required />
                                <input type="text" value={job.company} onChange={(e) => handleSectionChange('work_experiences', job.id, 'company', e.target.value)} placeholder="Company" className={sectionInputClasses} required />
                                <input type="text" value={job.location} onChange={(e) => handleSectionChange('work_experiences', job.id, 'location', e.target.value)} placeholder="Location" className={sectionInputClasses} required />
                                <input type="text" value={job.dates} onChange={(e) => handleSectionChange('work_experiences', job.id, 'dates', e.target.value)} placeholder="Dates (e.g., 2020 - 2024)" className={sectionInputClasses} required />
                            </div>
                            <textarea
                                value={job.responsibilities}
                                onChange={(e) => handleSectionChange('work_experiences', job.id, 'responsibilities', e.target.value)}
                                placeholder="Key responsibilities. Enter each point on a new line (will be converted to an array)."
                                rows="4" className={`${sectionInputClasses} w-full resize-y`} required
                            />
                        </div>
                    ))}
                    <button type="button" onClick={() => handleAddEntry('work_experiences')} className={dashedButton}>
                        + Add Work Experience Entry
                    </button>
                </div>

                {/* 3. EDUCATION SECTION */}
                <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800 border-b pb-2">3. Education</h2>
                <div className="space-y-6">
                    {(userDetails.educations || []).map((edu) => (
                        <div key={edu.id} className="p-5 border border-gray-200 rounded-xl bg-gray-50 relative shadow-sm">
                            <button type="button" onClick={() => handleRemoveEntry('educations', edu.id)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition duration-150 p-1 rounded-full bg-white" title="Remove Education Entry">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 10-2 0v6a1 1 0 102 0V8z" clipRule="evenodd" /></svg>
                            </button>
                            <h3 className="text-lg font-semibold mb-3 text-gray-700">Study: {edu.degree || 'New Degree'}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <input type="text" value={edu.degree} onChange={(e) => handleSectionChange('educations', edu.id, 'degree', e.target.value)} placeholder="Degree" className={sectionInputClasses} required />
                                <input type="text" value={edu.institution} onChange={(e) => handleSectionChange('educations', edu.id, 'institution', e.target.value)} placeholder="Institution" className={sectionInputClasses} required />
                                <input type="text" value={edu.location} onChange={(e) => handleSectionChange('educations', edu.id, 'location', e.target.value)} placeholder="Location" className={sectionInputClasses} required />
                                <input type="text" value={edu.dates} onChange={(e) => handleSectionChange('educations', edu.id, 'dates', e.target.value)} placeholder="Dates (e.g., 2015 - 2019)" className={sectionInputClasses} required />
                            </div>
                            <textarea
                                value={edu.achievements}
                                onChange={(e) => handleSectionChange('educations', edu.id, 'achievements', e.target.value)}
                                placeholder="Achievements (as a single string, e.g., Graduated with First Class Honors)"
                                rows="3" className={`${sectionInputClasses} w-full resize-y`}
                            />
                        </div>
                    ))}
                    <button type="button" onClick={() => handleAddEntry('educations')} className={dashedButton}>
                        + Add Education Entry
                    </button>
                </div>

                {/* 4. PROJECTS SECTION */}
                <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800 border-b pb-2">4. Projects</h2>
                <div className="space-y-6">
                    {(userDetails.projects || []).map((proj) => (
                        <div key={proj.id} className="p-5 border border-gray-200 rounded-xl bg-gray-50 relative shadow-sm">
                            <button type="button" onClick={() => handleRemoveEntry('projects', proj.id)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition duration-150 p-1 rounded-full bg-white" title="Remove Project Entry">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 10-2 0v6a1 1 0 102 0V8z" clipRule="evenodd" /></svg>
                            </button>
                            <h3 className="text-lg font-semibold mb-3 text-gray-700">Project: {proj.name || 'New Project'}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <input type="text" value={proj.name} onChange={(e) => handleSectionChange('projects', proj.id, 'name', e.target.value)} placeholder="Project Name" className={sectionInputClasses} required />
                                <input type="url" value={proj.url} onChange={(e) => handleSectionChange('projects', proj.id, 'url', e.target.value)} placeholder="Project URL" className={sectionInputClasses} />
                                <input type="text" value={proj.dates} onChange={(e) => handleSectionChange('projects', proj.id, 'dates', e.target.value)} placeholder="Dates (e.g., July, 2022)" className={sectionInputClasses} required />
                                <span className="hidden sm:block"></span>
                            </div>
                            <textarea
                                value={proj.summary}
                                onChange={(e) => handleSectionChange('projects', proj.id, 'summary', e.target.value)}
                                placeholder="Project Summary"
                                rows="3" className={`${sectionInputClasses} w-full resize-y`} required
                            />
                        </div>
                    ))}
                    <button type="button" onClick={() => handleAddEntry('projects')} className={dashedButton}>
                        + Add Project Entry
                    </button>
                </div>

                {/* 5. CERTIFICATIONS SECTION */}
                <h2 className="text-2xl font-bold mt-10 mb-4 text-gray-800 border-b pb-2">5. Certifications</h2>
                <div className="space-y-6">
                    {(userDetails.certifications || []).map((cert) => (
                        <div key={cert.id} className="p-5 border border-gray-200 rounded-xl bg-gray-50 relative shadow-sm">
                            <button type="button" onClick={() => handleRemoveEntry('certifications', cert.id)} className="absolute top-3 right-3 text-gray-400 hover:text-red-500 transition duration-150 p-1 rounded-full bg-white" title="Remove Certification Entry">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm6 0a1 1 0 10-2 0v6a1 1 0 102 0V8z" clipRule="evenodd" /></svg>
                            </button>
                            {/* { console.log(cert) } */}
                            <h3 className="text-lg font-semibold mb-3 text-gray-700">Cert: {cert.name || 'New Certification'}</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                <input type="text" value={cert.name} onChange={(e) => handleSectionChange('certifications', cert.id, 'name', e.target.value)} placeholder="Name" className={sectionInputClasses} required />
                                <input type="text" value={cert.certificate_id} onChange={(e) => handleSectionChange('certifications', cert.id, 'certificate_id', e.target.value)} placeholder="Certificate ID" className={sectionInputClasses} />
                                <input type="url" value={cert.url} onChange={(e) => handleSectionChange('certifications', cert.id, 'url', e.target.value)} placeholder="Certificate URL" className={sectionInputClasses} />
                                <input type="date" value={cert.dates} onChange={(e) => handleSectionChange('certifications', cert.id, 'dates', e.target.value)} placeholder="Dates (e.g., 02/02/2023)" className={sectionInputClasses} required />
                                <input type="text" value={cert.issuing_organization} onChange={(e) => handleSectionChange('certifications', cert.id, 'issuing_organization', e.target.value)} placeholder="Issuing Organization" className={sectionInputClasses} required />
                                <span className="hidden sm:block"></span>
                            </div>
                            <textarea
                                value={cert.summary}
                                onChange={(e) => handleSectionChange('certifications', cert.id, 'summary', e.target.value)}
                                placeholder="Certificate Summary"
                                rows="3" className={`${sectionInputClasses} w-full resize-y`} required
                            />
                        </div>
                    ))}
                    <button type="button" onClick={() => handleAddEntry('certifications')} className={dashedButton}>
                        + Add Certification Entry
                    </button>
                </div>


                {/* FORM SUBMISSION */}
                <div className="flex flex-col items-center mt-10">
                    <button
                        type="submit"
                        className={`px-12 py-3 rounded-full font-bold text-white shadow-xl ${primaryColor}`}
                        disabled={formStatus === 'loading_edit'}
                    >
                        {buttonText}
                    </button>
                </div>
            </form>
        </div>
    );
};




// --- 3. TEMPLATE PREVIEW COMPONENTS ---

const SectionTitle = ({ children }) => (
    <h4 className="text-lg font-bold mt-4 mb-2 uppercase text-[#198754] border-b border-[#198754]/50 pb-1">{children}</h4>
);

// --- Template 1: Simple & Clean (Classic, Single-Column) ---
const SimpleTemplate = ({ userDetails }) => (
    <div className="p-8 space-y-4 text-gray-800 text-sm">
        <header className="text-center pb-4 border-b-4 border-gray-900">
            <h1 className="text-3xl font-extrabold text-gray-900">{userDetails.full_name}</h1>
            <p className="text-sm text-gray-600 space-x-3 mt-1">
                <span>{userDetails.phone}</span> |
                <span>{userDetails.email}</span> |
                <span>{userDetails.city}, {userDetails.country}</span>
            </p>
            <p className="text-sm text-gray-600 space-x-3 mt-1">
                {userDetails.linkedin_url && <a href={userDetails.linkedin_url} target="_blank" className="hover:underline">{userDetails.linkedin_url}</a>} |
                {userDetails.portfolio_url && <a href={userDetails.portfolio_url} target="_blank" className="hover:underline">{userDetails.portfolio_url}</a>}
            </p>
        </header>

        {/* Summary */}
        <SectionTitle>Professional Summary</SectionTitle>
        <p>{userDetails.professional_summary}</p>

        {/* Experience */}
        <SectionTitle>Work Experience</SectionTitle>
        {userDetails.work_experiences.map(exp => (
            <div key={exp.id} className="mb-4">
                <div className="flex justify-between font-semibold">
                    <span>{exp.job_title} at {exp.company}</span>
                    <span className="text-xs text-gray-500">{exp.dates} | {exp.location}</span>
                </div>
                <ListRenderer text={exp.responsibilities} />
            </div>
        ))}

        {/* Projects */}
        <SectionTitle>Key Projects</SectionTitle>
        {userDetails.projects.map(proj => (
            <div key={proj.id} className="mb-4">
                <div className="flex justify-between font-semibold">
                    <span>{proj.name}</span>
                    <span className="text-xs text-gray-500">{proj.dates}</span>
                </div>
                <p className="italic">{proj.summary}</p>
                {proj.url && <a href={proj.url} target="_blank" className="text-xs text-[#198754] hover:underline">{proj.url}</a>}
            </div>
        ))}

        {/* Education */}
        <SectionTitle>Education</SectionTitle>
        {userDetails.educations.map(edu => (
            <div key={edu.id} className="mb-4">
                <div className="flex justify-between font-semibold">
                    <span>{edu.degree}, {edu.institution}</span>
                    <span className="text-xs text-gray-500">{edu.dates} | {edu.location}</span>
                </div>
                {edu.achievements && <p className="text-sm italic mt-1">{edu.achievements}</p>}
            </div>
        ))}

        {/* Certifications */}
        <SectionTitle>Certifications</SectionTitle>
        <ul className="grid grid-cols-2 gap-x-4">
            {userDetails.certifications.map(cert => (
                <li key={cert.id} className="mb-2">
                    <span className="font-semibold">{cert.name}</span> <span className="text-xs text-gray-500">({cert.dates})</span>
                    <p className="text-xs italic">{cert.issuing_organization}</p>
                </li>
            ))}
        </ul>
    </div>
);

// --- Template 2: ATS Optimized (Plain Structure) ---
const ATSTemplate = ({ userDetails }) => (
    <div className="p-8 space-y-6 font-mono text-gray-800 text-sm">
        <header className="text-center space-y-1">
            <h1 className="text-2xl font-bold uppercase">{userDetails.full_name}</h1>
            <p className="text-xs uppercase space-x-2 border-b border-gray-400 pb-2">
                <span>{userDetails.phone}</span> |
                <span>{userDetails.email}</span> |
                <span>{userDetails.city}, {userDetails.country}</span> |
                <span>{userDetails.linkedin_url}</span> |
                <span>{userDetails.portfolio_url}</span>
            </p>
        </header>

        {/* Summary */}
        <h2 className="text-lg font-bold uppercase border-b border-gray-800 pb-1">Summary</h2>
        <p>{userDetails.professional_summary}</p>

        {/* Experience */}
        <h2 className="text-lg font-bold uppercase border-b border-gray-800 pb-1">Experience</h2>
        {userDetails.work_experiences.map(exp => (
            <div key={exp.id} className="mb-4">
                <div className="flex justify-between font-bold">
                    <span>{exp.job_title} - {exp.company}</span>
                    <span className="text-xs">{exp.dates}</span>
                </div>
                <p className="text-xs mb-1">{exp.location}</p>
                <ListRenderer text={exp.responsibilities} />
            </div>
        ))}

        {/* Projects */}
        <h2 className="text-lg font-bold uppercase border-b border-gray-800 pb-1">Projects</h2>
        {userDetails.projects.map(proj => (
            <div key={proj.id} className="mb-4">
                <div className="flex justify-between font-bold">
                    <span>{proj.name}</span>
                    <span className="text-xs">{proj.dates}</span>
                </div>
                <p className="text-sm">{proj.summary}</p>
                {proj.url && <p className="text-xs">URL: {proj.url}</p>}
            </div>
        ))}

        {/* Education */}
        <h2 className="text-lg font-bold uppercase border-b border-gray-800 pb-1">Education</h2>
        {userDetails.educations.map(edu => (
            <div key={edu.id} className="mb-4">
                <div className="flex justify-between font-bold">
                    <span>{edu.degree}, {edu.institution}</span>
                    <span className="text-xs">{edu.dates}</span>
                </div>
                <p className="text-xs">{edu.location}</p>
                {edu.achievements && <p className="text-sm">{edu.achievements}</p>}
            </div>
        ))}

        {/* Certifications */}
        <h2 className="text-lg font-bold uppercase border-b border-gray-800 pb-1">Certifications</h2>
        <div className="space-y-2">
            {userDetails.certifications.map(cert => (
                <div key={cert.id} className="text-sm">
                    <span className="font-bold">{cert.name}</span> ({cert.dates})
                    <span className="mx-2">|</span> {cert.issuing_organization}
                </div>
            ))}
        </div>
    </div>
);

// --- Template 3: Modern & Bold (Two-Column) ---
const ModernTemplate = ({ userDetails }) => (
    <div className="grid grid-cols-4 h-full text-gray-800 text-sm">
        {/* Left Sidebar (25%) */}
        <div className="col-span-1 bg-gray-900 text-white p-6 space-y-6">
            <div className="text-center">
                <h1 className="text-2xl font-extrabold">{userDetails.full_name.split(' ')[0]}</h1>
                <h1 className="text-2xl font-extrabold -mt-1">{userDetails.full_name.split(' ').slice(1).join(' ')}</h1>
                <p className="text-xs font-light text-[#198754] uppercase tracking-wider mt-1">{userDetails.title}</p>
            </div>

            <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-widest border-b border-gray-600 pb-1 text-[#198754]">Contact</h3>
                <div className="text-xs space-y-1">
                    <p>{userDetails.phone}</p>
                    <p>{userDetails.email}</p>
                    <p>{userDetails.city}, {userDetails.country}</p>
                    <p>{userDetails.home_address}</p>
                </div>
            </div>

            <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-widest border-b border-gray-600 pb-1 text-[#198754]">Links</h3>
                <div className="text-xs space-y-1">
                    {userDetails.linkedin_url && <a href={userDetails.linkedin_url} target="_blank" className="hover:underline block">{userDetails.linkedin_url}</a>}
                    {userDetails.portfolio_url && <a href={userDetails.portfolio_url} target="_blank" className="hover:underline block">{userDetails.portfolio_url}</a>}
                </div>
            </div>

            <div className="space-y-3">
                <h3 className="text-xs font-bold uppercase tracking-widest border-b border-gray-600 pb-1 text-[#198754]">Certifications</h3>
                <div className="text-xs space-y-2">
                    {userDetails.certifications.map(cert => (
                        <div key={cert.id}>
                            <p className="font-semibold">{cert.name}</p>
                            <p className="text-gray-400 italic">{cert.issuing_organization}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>

        {/* Right Content (75%) */}
        <div className="col-span-3 p-6 space-y-6">

            {/* Summary */}
            <div className="pb-3 border-b border-gray-300">
                <h3 className="text-xl font-extrabold mb-1 uppercase text-gray-700">Profile Summary</h3>
                <p className="text-sm">{userDetails.professional_summary}</p>
            </div>

            {/* Experience */}
            <div className="pb-3 border-b border-gray-300">
                <h3 className="text-xl font-extrabold mb-3 uppercase text-gray-700">Experience</h3>
                {userDetails.work_experiences.map(exp => (
                    <div key={exp.id} className="mb-4">
                        <div className="flex justify-between items-baseline">
                            <h4 className="font-bold text-base text-[#198754]">{exp.job_title}</h4>
                            <span className="text-xs font-medium">{exp.dates}</span>
                        </div>
                        <p className="text-sm italic">{exp.company} | {exp.location}</p>
                        <ListRenderer text={exp.responsibilities} />
                    </div>
                ))}
            </div>

            {/* Projects */}
            <div className="pb-3 border-b border-gray-300">
                <h3 className="text-xl font-extrabold mb-3 uppercase text-gray-700">Projects</h3>
                {userDetails.projects.map(proj => (
                    <div key={proj.id} className="mb-4">
                        <div className="flex justify-between items-baseline">
                            <h4 className="font-bold text-base text-gray-700">{proj.name}</h4>
                            <span className="text-xs font-medium">{proj.dates}</span>
                        </div>
                        <p className="text-sm">{proj.summary}</p>
                        {proj.url && <a href={proj.url} target="_blank" className="text-xs text-[#198754] hover:underline">Link</a>}
                    </div>
                ))}
            </div>

            {/* Education */}
            <div className="pb-3">
                <h3 className="text-xl font-extrabold mb-3 uppercase text-gray-700">Education</h3>
                {userDetails.educations.map(edu => (
                    <div key={edu.id} className="mb-4">
                        <div className="flex justify-between items-baseline">
                            <h4 className="font-bold text-base text-gray-700">{edu.degree}</h4>
                            <span className="text-xs font-medium">{edu.dates}</span>
                        </div>
                        <p className="text-sm italic">{edu.institution} | {edu.location}</p>
                        {edu.achievements && <p className="text-xs mt-1">{edu.achievements}</p>}
                    </div>
                ))}
            </div>
        </div>
    </div>
);


// --- 4. STEP 2: TEMPLATE SELECTION AND CV CREATION (LIVE API Call) ---

// const API_BASE_URL = 'https://test.signup.com.ng/v1/api'; 
const API_BASE_URL = 'https://cv.signup.com.ng/v1/api';
const CV_CREATE_ENDPOINT = `${API_BASE_URL}/cvs`;

const CVTemplatesPage = ({ navigate }) => {
    const { userDetails, setUserDetails, templates } = useCVContext();
    const [selectedTemplate, setSelectedTemplate] = useState(userDetails.template_id || 3);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoading2, setIsLoading2] = useState(false);
    const [statusMessage, setStatusMessage] = useState(null);

    const { auth } = useAuth()
    const API_TOKEN = auth.token

    // Update context when a template is selected
    useEffect(() => {
        setUserDetails(prev => ({ ...prev, template_id: selectedTemplate }));
    }, [selectedTemplate, setUserDetails]);

    // Determine which Template component to render
    const renderTemplatePreview = () => {
        const template = templates.find(t => t.id === selectedTemplate);
        if (!template) return <p className='p-8 text-center text-red-500'>Template not found.</p>;

        switch (template.type) {
            case 'Simple':
                return <SimpleTemplate userDetails={userDetails} />;
            case 'ATS':
                return <ATSTemplate userDetails={userDetails} />;
            case 'Modern':
            default:
                return <ModernTemplate userDetails={userDetails} />;
        }
    }

    // Handler to construct API payload and perform LIVE fetch request
    const handleCreateCV = async () => {
        const API_TOKEN = auth.token
        setIsLoading(true);
        setStatusMessage(null);

        // 1. Transform complex arrays to match API structure 
        const transformedWorkExperiences = (userDetails.work_experiences || []).map(exp => ({
            job_title: exp.job_title,
            company: exp.company,
            location: exp.location,
            dates: exp.dates,
            responsibilities: exp.responsibilities ? exp.responsibilities.split('\n').filter(line => line.trim() !== '') : [],
        }));

        const transformedEducations = (userDetails.educations || []).map(edu => ({
            degree: edu.degree,
            institution: edu.institution,
            location: edu.location,
            dates: edu.dates,
            achievements: edu.achievements || '',
        }));

        const transformedProjects = (userDetails.projects || []).map(p => ({
            name: p.name,
            url: p.url,
            dates: p.dates,
            summary: p.summary,
        }));

        const transformedCertifications = (userDetails.certifications || []).map(c => ({
            name: c.name,
            certificate_id: c.certificate_id,
            url: c.url,
            dates: c.dates,
            issuing_organization: c.issuing_organization,
        }));


        // 2. Construct the final API payload
        const finalPayload = {
            title: userDetails.title,
            template_id: selectedTemplate,
            full_name: userDetails.full_name,
            email: userDetails.email,
            phone: userDetails.phone,
            country: userDetails.country,
            city: userDetails.city,
            home_address: userDetails.home_address,
            postal_code: userDetails.postal_code,
            linkedin_url: userDetails.linkedin_url,
            portfolio_url: userDetails.portfolio_url,
            professional_summary: userDetails.professional_summary,
            work_experiences: transformedWorkExperiences,
            educations: transformedEducations,
            projects: transformedProjects,
            certifications: transformedCertifications,
        };

        console.log("--- API PAYLOAD SENT ---", finalPayload);
        console.log(`--- TARGET URL --- ${CV_CREATE_ENDPOINT}`);
        console.log(`--- AUTHORIZATION TOKEN --- Bearer ${userDetails.apiToken.substring(0, 10)}...`);

        try {
            // --- LIVE API CALL TO CREATE CV ENDPOINT ---
            const response = await fetch(CV_CREATE_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    // Use the token provided by the user
                    'Authorization': `Bearer ${API_TOKEN}`,
                },
                body: JSON.stringify(finalPayload),
            });

            const responseData = await response.json();

            if (!response.ok) {
                // Handle API errors (4xx, 5xx)
                console.error("API Error Response:", responseData);
                throw new Error(`API Error: ${response.status} - ${responseData.message || 'Failed to create CV. Check console for details.'}`);
            }

            const createdCvId = responseData.data.id || 'UNKNOWN_ID';
            console.log("CV Created Successfully. ID:", createdCvId);

            // Store the ID for the download step
            setUserDetails(prev => ({ ...prev, createdCvId: createdCvId }));

            setStatusMessage('success');
            navigate(3); // Go to Download Page (Step 3)

        } catch (error) {
            console.error("CV Creation error:", error.message);
            setStatusMessage('error');
            setIsLoading(false);
        }
    };

    const handleCreateCVNoDownload = async () => {
        setIsLoading2(true);
        setStatusMessage(null);

        // 1. Transform complex arrays to match API structure 
        const transformedWorkExperiences = (userDetails.work_experiences || []).map(exp => ({
            job_title: exp.job_title,
            company: exp.company,
            location: exp.location,
            dates: exp.dates,
            responsibilities: exp.responsibilities ? exp.responsibilities.split('\n').filter(line => line.trim() !== '') : [],
        }));

        const transformedEducations = (userDetails.educations || []).map(edu => ({
            degree: edu.degree,
            institution: edu.institution,
            location: edu.location,
            dates: edu.dates,
            achievements: edu.achievements || '',
        }));

        const transformedProjects = (userDetails.projects || []).map(p => ({
            name: p.name,
            url: p.url,
            dates: p.dates,
            summary: p.summary,
        }));

        const transformedCertifications = (userDetails.certifications || []).map(c => ({
            name: c.name,
            certificate_id: c.certificate_id,
            url: c.url,
            dates: c.dates,
            issuing_organization: c.issuing_organization,
        }));


        // 2. Construct the final API payload
        const finalPayload = {
            title: userDetails.title,
            template_id: selectedTemplate,
            full_name: userDetails.full_name,
            email: userDetails.email,
            phone: userDetails.phone,
            country: userDetails.country,
            city: userDetails.city,
            home_address: userDetails.home_address,
            postal_code: userDetails.postal_code,
            linkedin_url: userDetails.linkedin_url,
            portfolio_url: userDetails.portfolio_url,
            professional_summary: userDetails.professional_summary,
            work_experiences: transformedWorkExperiences,
            educations: transformedEducations,
            projects: transformedProjects,
            certifications: transformedCertifications,
        };

        console.log("--- API PAYLOAD SENT ---", finalPayload);
        console.log(`--- TARGET URL --- ${CV_CREATE_ENDPOINT}`);
        console.log(`--- AUTHORIZATION TOKEN --- Bearer ${userDetails.apiToken.substring(0, 10)}...`);

        try {
            // --- LIVE API CALL TO SAVE CV ENDPOINT ---
            const response = await fetch(CV_CREATE_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${API_TOKEN}`,
                },
                body: JSON.stringify(finalPayload),
            });

            const responseData = await response.json();
            // console.log(responseData.data)
            // console.log(responseData.data.id)

            if (!response.ok) {
                // Handle API errors (4xx, 5xx)
                console.error("API Error Response:", responseData);
                throw new Error(`API Error: ${response.status} - ${responseData.message || 'Failed to create CV. Check console for details.'}`);
            }

            const createdCvId = responseData.data.id || 'UNKNOWN_ID';
            console.log("CV Created Successfully. ID:", createdCvId);

            // Store the ID for the download step
            setUserDetails(prev => ({ ...prev, createdCvId: createdCvId }));

            setStatusMessage('success');
            alert("CV has been saved.");
            setIsLoading2(false);

            // User chooses to only Save CV.
            // navigate(3); // Go to Download Page (Step 3)

        } catch (error) {
            console.error("CV Creation error:", error.message);
            setStatusMessage('error');
            setIsLoading2(false);
        }
    };

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col items-center p-4 sm:p-8">
            <div className="w-full max-w-5xl">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-3xl font-extrabold text-gray-900">Step 2: Template Selection & Creation</h1>
                    <span className="text-sm font-semibold text-white bg-[#198754] px-4 py-1 rounded-full shadow-md">2/3</span>
                </div>
            </div>

            <button onClick={() => navigate(4)} className="text-sm text-[#198754] hover:underline flex items-center mt-2">
                Go to User Dashboard and See a list of CVs you have created in the past instead.
            </button>
            <br />

            {/* Warning Banner */}
            <div className="w-full max-w-5xl p-4 bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 mb-6 rounded-lg shadow-md" role="alert">
                <p className="font-bold">IMPORTANT: Live API Call</p>
                <p className="text-sm">
                    1. Clicking <b>**"Create CV"**</b> will send a live `POST` request to `{CV_CREATE_ENDPOINT}` using the signed-in details of the User.
                    <br /> 2. Clicking on <b>**"Save CV"**</b>  will only save the User's CV to our System.
                    <br /> <b>Note: To Download CV is not free but to save CV is completely free </b>
                </p>
            </div>


            <div className="w-full max-w-5xl grid grid-cols-1 lg:grid-cols-3 gap-8 mb-10">

                {/* Template Selection Section */}
                <div className="lg:col-span-1 space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">Select Template</h2>
                    <div className="space-y-3">
                        {templates.map(template => (
                            <div
                                key={template.id}
                                onClick={() => setSelectedTemplate(template.id)}
                                className={`p-4 border-2 rounded-lg cursor-pointer transition duration-150 ${selectedTemplate === template.id ? 'border-4 border-[#198754] bg-[#e6f2ed] shadow-md' : 'border-gray-300 hover:border-gray-400'}`}
                            >
                                <p className="font-bold text-lg">{template.name}</p>
                                <p className="text-sm text-gray-600">{template.description}</p>
                                <span className="text-xs font-medium text-[#198754] mt-1 block">Template ID: {template.id} ({template.type})</span>
                            </div>
                        ))}
                    </div>
                    <button onClick={() => navigate(1)} className="text-sm text-[#198754] hover:underline flex items-center mt-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M11 17l-5-5m0 0l5-5m-5 5h12" /></svg>
                        Go Back to Edit Details (Step 1)
                    </button>
                </div>

                {/* CV Preview Section */}
                <div className="lg:col-span-2 space-y-4">
                    <h2 className="text-xl font-bold text-gray-800">Live Preview: {templates.find(t => t.id === selectedTemplate)?.name}</h2>
                    <div className="p-1 border border-gray-300 rounded-lg bg-white shadow-xl max-h-[80vh] overflow-y-auto">
                        {renderTemplatePreview()}
                    </div>
                </div>
            </div>

            {/* Final Submission Button */}
            <div className="w-full max-w-5xl flex justify-center">
                <button
                    onClick={handleCreateCV}
                    className={`px-12 py-3 rounded-full font-bold text-white shadow-xl flex items-center space-x-2 ${isLoading ? 'bg-gray-400 cursor-not-allowed' : primaryColor}`}
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            <span>Creating CV via API...</span>
                        </>
                    ) : (
                        `Create CV & Proceed to Download (Step 3)`
                    )}
                </button>

                <br />
                <br />
                <br />

                {/* User saving CV to database instead and no direction */}
                <button
                    onClick={handleCreateCVNoDownload}
                    className={` ml-8 px-12 py-3 rounded-full font-bold text-white shadow-xl flex items-center space-x-2 ${isLoading2 ? 'bg-gray-400 cursor-not-allowed' : primaryColor}`}
                    disabled={isLoading2}
                >
                    {isLoading2 ? (
                        <>
                            <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
                            <span>Saving CV to System...</span>
                        </>
                    ) : (
                        `Save CV`
                    )}


                </button>
            </div>

            {statusMessage === 'error' && (
                <div className="mt-4 p-3 text-sm text-red-800 rounded-lg bg-red-50 w-full max-w-5xl" role="alert">
                    <p className='font-bold'>Creation Failed!</p>
                    <p>An error occurred during the API call. Please check your **API Token**, verify the **API URL** in the code, and examine the browser console for specific error details.</p>
                </div>
            )}
        </div>
    );
};


// --- 5. STEP 3: CV DOWNLOAD PAGE ---

const CVDownloadPage = ({ navigate }) => {
    const { userDetails } = useCVContext();

    // In a real application, you would use userDetails.createdCvId to fetch the PDF/File link
    // For now, we simulate the link using the created ID.
    const mockDownloadUrl = `${API_BASE_URL}/cvs/${userDetails.createdCvId}/download`;

    return (
        <div className="bg-gray-50 min-h-screen flex flex-col items-center justify-center p-4">
            <div className="bg-white p-10 rounded-xl shadow-2xl w-full max-w-xl text-center">
                <div className="flex justify-center items-center mb-6">
                    <h1 className="text-3xl font-extrabold text-gray-900 mr-4">Step 3: Download CV</h1>
                    <span className="text-sm font-semibold text-white bg-[#198754] px-4 py-1 rounded-full shadow-md">3/3</span>
                </div>

                <svg xmlns="http://www.w3.org/2000/svg" className="h-20 w-20 text-[#198754] mx-auto mb-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>

                <h2 className="text-2xl font-bold text-gray-800 mb-2">CV Created Successfully!</h2>
                <p className="text-gray-600 mb-4">
                    Your CV, <b>{userDetails.title}</b>, has been generated (ID: <b>{userDetails.createdCvId}</b>).
                </p>
                {/* <p className="text-sm text-gray-500 mb-8">
                    You can now use your backend's `GET /cvs/{userDetails.id}` or a download endpoint with the ID **{userDetails.createdCvId}** to retrieve the file.
                </p> */}

                <a
                    href={mockDownloadUrl}
                    // For demonstration, we'll keep the download link as a simple text file
                    download={`${userDetails.full_name.replace(/\s/g, '_')}_CV_ID_${userDetails.createdCvId}.txt`}
                    className={`w-full sm:w-auto px-8 py-3 rounded-full font-bold text-white shadow-xl ${primaryColor} mb-4 block sm:inline-block`}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Download CV File (Mock Link)
                </a>

                <button
                    onClick={() => navigate(4)}
                    className="w-full text-lg font-semibold px-6 py-3 rounded-xl shadow-lg transition duration-300 border-2 border-green-700 bg-white text-green-700 hover:bg-green-50"
                >
                    Go to User Dashboard &rarr;
                </button>

                <button
                    onClick={() => navigate(1)}
                    className={`w-full sm:w-auto mt-4 ${secondaryButton}`}
                >
                    Start New CV (Go to Step 1)
                </button>
            </div>
        </div>
    );
};


// --- 6. MAIN APP COMPONENT (Router Simulation) ---

// const App = () => {
//     const [navigationState, setNavigationState] = useState({
//         step: 1,
//         cvIdToEdit: null,
//     });
//     // const navigate = (newStep) => setStep(newStep);

//     const navigate = (newStep, cvId = null) => {
//         setNavigationState({
//             step: newStep,
//             cvIdToEdit: cvId,
//         });
//     };

//     const renderStep = () => {
//         switch (navigationState.step) {
//             case 1:
//                 return (
//                     <CVFormPage
//                         navigate={navigate}
//                         cvIdToEdit={navigationState.cvIdToEdit} // Pass the ID
//                     />
//                 );
//             case 2:
//                 return <CVTemplatesPage navigate={navigate} />;
//             case 3:
//                 return <CVDownloadPage navigate={navigate} />;
//             case 4:
//                 return <UserDashboardPage navigate={navigate} />;
//             default:
//                 return <CVFormPage navigate={navigate} cvIdToEdit={navigationState.cvIdToEdit} />;
//         }
//     }

//     return (
//         // Include the necessary Tailwind CSS script for styling
//         <>
//             <script src="https://cdn.tailwindcss.com"></script>
//             <style>{`
//                 @import url('https://fonts.googleapis.com/css2?family=Inter:wght@100..900&display=swap');
//                 .font-inter {
//                     font-family: 'Inter', sans-serif;
//                 }
//             `}</style>
//             {/* <LandingPage /> */}
//             <CVProvider>
//                 <div className="font-inter">
//                     {renderStep()}
//                 </div>
//             </CVProvider>
//         </>
//     );
// };

// export default App;

// --- AUTHENTICATION CONTEXT ---
// const AuthContext = React.createContext(null);
// const useAuth = () => React.useContext(AuthContext); // Custom hook to use AuthContext

const AuthProvider = ({ children }) => {
    const [auth, setAuth] = useState(() => {
        // Initialize state from localStorage
        const token = localStorage.getItem('auth_token');
        const userData = localStorage.getItem('user_data');
        return {
            isAuthenticated: !!token,
            token: token,
            user: userData ? JSON.parse(userData) : null
        };
    });

    const logout = () => {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        setAuth({
            isAuthenticated: false,
            token: null,
            user: null
        });
    };

    const value = { auth, setAuth, logout };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};


// --- AuthComponent.js (Conceptual - put these functions/components into App.js for the one-file requirement) ---
// const AUTH_API_URL = 'https://test.signup.com.ng/v1/api/auth';
const AUTH_API_URL = 'https://cv.signup.com.ng/v1/api/auth';
// const inputClasses = "w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#198754] focus:border-transparent transition duration-150";

// A utility function to save the token and user details upon successful auth
const saveAuthDetails = (token, userDetails, setAuth) => {
    localStorage.setItem('auth_token', token);
    localStorage.setItem('user_data', JSON.stringify(userDetails));
    setAuth({
        isAuthenticated: true,
        token: token,
        user: userDetails
    });
};

const actualUserBalance = 0;

// --- LoginForm Component ---
const LoginForm = ({ setAuth, navigate }) => {
    const [email, setemail] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [status, setStatus] = React.useState(null); // 'loading', 'error'

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');

        try {
            const response = await fetch(`${AUTH_API_URL}/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });

            const data = await response.json();

            if (!response.ok) {
                // Assuming API returns an error message in the data object
                throw new Error(data.message || 'Login failed.');
            }

            // Assuming the API returns a token and user details upon success
            const token = data.token; // Get the Bearer token
            const userDetails = data.user; // Get user details

            saveAuthDetails(token, userDetails, setAuth);
            console.log(userDetails);
            // actualUserBalance = userDetails.balance;
            setStatus('success');
            navigate(1); // Navigate to CVFormPage (Step 1)

        } catch (error) {
            console.error('Login Error:', error);
            setStatus('error');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-6 sm:p-10 bg-white rounded-xl shadow-2xl max-w-sm mx-auto my-10 space-y-4">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-6">🔑 Login</h2>
            {status === 'error' && (
                <div className="p-3 text-sm text-red-800 rounded-lg bg-red-50">Login failed. Please check your credentials.</div>
            )}

            <input type="email" value={email} onChange={(e) => setemail(e.target.value)} placeholder="Email" className={inputClasses} required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputClasses} required />

            <button type="submit" disabled={status === 'loading'} className="w-full py-3 rounded-lg font-bold text-white bg-[#198754] hover:bg-[#156e43] transition duration-150 shadow-md">
                {status === 'loading' ? 'Logging in...' : 'Log In'}
            </button>
            <p className="text-center text-sm text-gray-600">
                Don't have an account?{' '}
                <button type="button" onClick={() => navigate('signup')} className="text-[#198754] hover:underline font-medium">Sign Up</button>
            </p>
        </form>
    );
};

// console.log(actualUserBalance);

// --- SignupForm Component ---
const SignupForm = ({ setAuth, navigate }) => {
    const [firstname, setfirstname] = useState('')
    const [lastname, setlastname] = useState('')
    const [email, setemail] = React.useState('');
    const [phone, setphone] = React.useState('');
    const [password, setPassword] = React.useState('');
    const [password_confirmation, setpassword_confirmation] = React.useState('');
    const [status, setStatus] = React.useState(null); // 'loading', 'error'

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');

        try {
            const response = await fetch(`${AUTH_API_URL}/register`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ firstname, lastname, email, phone, password, password_confirmation }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.message || 'Signup failed.');
            }

            // Assuming registration also returns a token and user details

            // const token = data.token;
            // const userDetails = data.user;
            // saveAuthDetails(token, userDetails, setAuth);

            // Removing the lines above because we no longer automatically log the user in after signup. 
            // They must verify their email first and then log in.

            setStatus('success');
            navigate('request-email'); // Navigate to Another Page to request verification email

        } catch (error) {
            console.error('Signup Error:', error);
            setStatus('error');
        }
    };

    return (
        <form onSubmit={handleSubmit} className="p-6 sm:p-10 bg-white rounded-xl shadow-2xl max-w-sm mx-auto my-10 space-y-4">
            <h2 className="text-3xl font-extrabold text-gray-900 text-center mb-6">📝 Sign Up</h2>
            {status === 'error' && (
                <div className="p-3 text-sm text-red-800 rounded-lg bg-red-50">Signup failed. {status === 'loading' ? '' : 'User may already exist or there was a server error.'}</div>
            )}

            <input type="text" value={firstname} onChange={(e) => setfirstname(e.target.value)} placeholder="First Name" className={inputClasses} required />
            <input type="text" value={lastname} onChange={(e) => setlastname(e.target.value)} placeholder="Last Name" className={inputClasses} required />
            <input type="email" value={email} onChange={(e) => setemail(e.target.value)} placeholder="Email" className={inputClasses} required />
            <input type="number" value={phone} onChange={(e) => setphone(e.target.value)} placeholder="Phone Number" className={inputClasses} required />
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputClasses} required />
            <input type="password" value={password_confirmation} onChange={(e) => setpassword_confirmation(e.target.value)} placeholder="Confirm Password" className={inputClasses} required />


            <button type="submit" disabled={status === 'loading'} className="w-full py-3 rounded-lg font-bold text-white bg-[#198754] hover:bg-[#156e43] transition duration-150 shadow-md">
                {status === 'loading' ? 'Signing up...' : 'Sign Up'}
            </button>
            <p className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <button type="button" onClick={() => navigate('login')} className="text-[#198754] hover:underline font-medium">Log In</button>
            </p>
        </form>
    );
};

// Assuming your API endpoint for sending the verification email is defined here
// const VERIFICATION_SEND_ENDPOINT = 'https://test.signup.com.ng/v1/api/auth/email/resend';
const VERIFICATION_SEND_ENDPOINT = 'https://cv.signup.com.ng/v1/api/auth/email/resend';

const EmailRequestForm = ({ navigate }) => {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: null, text: '' });

    const handleSendVerificationEmail = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setStatusMessage({ type: null, text: '' });

        try {
            const response = await fetch(VERIFICATION_SEND_ENDPOINT, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }), // Send the entered email
            });

            const responseData = await response.json();

            if (!response.ok) {
                // Handle API errors (e.g., email not found, already verified, etc.)
                throw new Error(responseData.message || `HTTP Error: ${response.status}`);
            }

            // Success: Email sent successfully
            setStatusMessage({
                type: 'success',
                text: 'Verification email successfully sent! Please check your inbox.'
            });

            // After success, navigate to a confirmation page or prompt for login
            // For now, let's keep the user on the page and show success

        } catch (error) {
            console.error("Verification email request failed:", error);
            setStatusMessage({
                type: 'error',
                text: error.message || 'Failed to send verification email. Please try again.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="max-w-md mx-auto p-8 border rounded-xl shadow-lg bg-white">
            <h2 className="text-3xl font-bold mb-6 text-gray-900">Request Verification Email</h2>
            <p className="mb-6 text-gray-600">
                To complete your registration, please enter your email address to receive the verification link.
            </p>

            <form onSubmit={handleSendVerificationEmail}>
                <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email Address</label>
                    <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#198754] focus:border-[#198754]"
                        placeholder="your.email@example.com"
                        disabled={isLoading}
                    />
                </div>

                {statusMessage.text && (
                    <div className={`p-3 rounded-md mb-4 ${statusMessage.type === 'error' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {statusMessage.text}
                    </div>
                )}

                <button
                    type="submit"
                    className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white transition duration-200 ${isLoading ? 'bg-gray-400' : 'bg-[#198754] hover:bg-green-700'}`}
                    disabled={isLoading}
                >
                    {isLoading ? 'Sending...' : 'Send Verification Email'}
                </button>
            </form>

            <p className="mt-6 text-center text-sm">
                Already verified? <button onClick={() => navigate('login')} className="text-[#198754] hover:underline">Go to Sign In</button>
            </p>
        </div>
    );
};



// --- MAIN APP COMPONENT ---
const App = () => {
    // We use a custom string state for login/signup, and numbers for main app steps
    const [navigationState, setNavigationState] = useState({
        step: 'login', // Initial step is 'login' or 'signup'
        cvIdToEdit: null
    });

    const { auth, logout, setAuth } = useAuth(); // Get auth state and functions

    // Use a unified navigate function
    const navigate = (newStep, cvId = null) => {
        setNavigationState({ step: newStep, cvIdToEdit: cvId });
    };

    // Redirect to the dashboard if a token exists but the user is on login/signup page
    useEffect(() => {
        if (auth.isAuthenticated && (navigationState.step === 'login' || navigationState.step === 'signup')) {
            navigate(4); // Navigate to dashboard (Step 4) after successful login
        } else if (!auth.isAuthenticated && navigationState.step !== 'login' && navigationState.step !== 'signup') {
            navigate('login'); // Redirect to login if a token is lost or doesn't exist
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [auth.isAuthenticated]);

    // Conditionally render based on authentication status and step
    const renderStep = () => {
        // If not authenticated, show login/signup forms only
        if (!auth.isAuthenticated) {
            if (navigationState.step === 'signup') {
                return <SignupForm setAuth={setAuth} navigate={navigate} />;
            }
            // 💡 Add the new step here:
            if (navigationState.step === 'request-email') {
                return <EmailRequestForm navigate={navigate} />;
            }
            // Default to login
            return <LoginForm setAuth={setAuth} navigate={navigate} />;
        }

        // If authenticated, render application steps
        switch (navigationState.step) {
            case 1:
                return (
                    // CVFormPage must now use the token from the AuthContext internally
                    <CVFormPage
                        navigate={navigate}
                        cvIdToEdit={navigationState.cvIdToEdit}
                    />
                );
            case 2:
                return <CVTemplatesPage navigate={navigate} />;
            case 3:
                return <CVDownloadPage navigate={navigate} />;
            case 4:
                return <UserDashboardPage navigate={navigate} />;
            default:
                return <CVFormPage navigate={navigate} cvIdToEdit={navigationState.cvIdToEdit} />;
        }
    }

    return (
        <>
            <div className="p-4 flex justify-end bg-gray-100">
                {auth.isAuthenticated && (
                    <button
                        onClick={logout}
                        className="text-sm font-semibold text-white bg-red-500 hover:bg-red-600 px-3 py-1 rounded-full shadow-md transition duration-150"
                    >
                        Logout
                    </button>
                )}
            </div>
            {renderStep()}
        </>
    );
}

// --- FINAL WRAPPER ---
// The AuthProvider wraps the main application to make the auth state available
const RootApp = () => (
    <AuthProvider>
        <CVProvider>
            <App />
        </CVProvider>
    </AuthProvider>
);

export default RootApp;

// Add the LoginForm and SignupForm components from Section 1 here, and your CVFormPage etc.
// NOTE: For a single file, you'd put all the JSX components (CVFormPage, CVTemplatesPage, etc.)
// right into this file as well.